'use client';

import React, { useEffect, useState } from 'react';
import { CheckCircle, X, AlertCircle, Info, AlertTriangle } from 'lucide-react';

interface ToastProps {
  message: string;
  isVisible: boolean;
  onClose: () => void;
  duration?: number;
  type?: 'success' | 'error' | 'info' | 'warning';
}

const Toast: React.FC<ToastProps> = ({ 
  message, 
  isVisible, 
  onClose, 
  duration = 3000,
  type = 'success' 
}) => {
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (isVisible) {
      setIsAnimating(true);
      const timer = setTimeout(() => {
        setIsAnimating(false);
        setTimeout(onClose, 300); // Esperar a que termine la animación
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [isVisible, duration, onClose]);

  if (!isVisible && !isAnimating) return null;

  const getToastStyles = () => {
    switch (type) {
      case 'success':
        return {
          bgColor: 'bg-green-500',
          hoverColor: 'hover:bg-green-600',
          icon: <CheckCircle data-component-id="src/components/ui/Toast.tsx:43:16" data-component-path="src/components/ui/Toast.tsx" data-component-line="43" data-component-file="Toast.tsx" data-component-name="CheckCircle" data-component-content="%7B%22elementName%22%3A%22CheckCircle%22%2C%22className%22%3A%22w-5%20h-5%20flex-shrink-0%22%7D" className="w-5 h-5 flex-shrink-0" />
        };
      case 'error':
        return {
          bgColor: 'bg-red-500',
          hoverColor: 'hover:bg-red-600',
          icon: <AlertCircle data-component-id="src/components/ui/Toast.tsx:49:16" data-component-path="src/components/ui/Toast.tsx" data-component-line="49" data-component-file="Toast.tsx" data-component-name="AlertCircle" data-component-content="%7B%22elementName%22%3A%22AlertCircle%22%2C%22className%22%3A%22w-5%20h-5%20flex-shrink-0%22%7D" className="w-5 h-5 flex-shrink-0" />
        };
      case 'warning':
        return {
          bgColor: 'bg-orange-500',
          hoverColor: 'hover:bg-orange-600',
          icon: <AlertTriangle data-component-id="src/components/ui/Toast.tsx:55:16" data-component-path="src/components/ui/Toast.tsx" data-component-line="55" data-component-file="Toast.tsx" data-component-name="AlertTriangle" data-component-content="%7B%22elementName%22%3A%22AlertTriangle%22%2C%22className%22%3A%22w-5%20h-5%20flex-shrink-0%22%7D" className="w-5 h-5 flex-shrink-0" />
        };
      case 'info':
        return {
          bgColor: 'bg-blue-500',
          hoverColor: 'hover:bg-blue-600',
          icon: <Info data-component-id="src/components/ui/Toast.tsx:61:16" data-component-path="src/components/ui/Toast.tsx" data-component-line="61" data-component-file="Toast.tsx" data-component-name="Info" data-component-content="%7B%22elementName%22%3A%22Info%22%2C%22className%22%3A%22w-5%20h-5%20flex-shrink-0%22%7D" className="w-5 h-5 flex-shrink-0" />
        };
      default:
        return {
          bgColor: 'bg-green-500',
          hoverColor: 'hover:bg-green-600',
          icon: <CheckCircle data-component-id="src/components/ui/Toast.tsx:67:16" data-component-path="src/components/ui/Toast.tsx" data-component-line="67" data-component-file="Toast.tsx" data-component-name="CheckCircle" data-component-content="%7B%22elementName%22%3A%22CheckCircle%22%2C%22className%22%3A%22w-5%20h-5%20flex-shrink-0%22%7D" className="w-5 h-5 flex-shrink-0" />
        };
    }
  };

  const { bgColor, hoverColor, icon } = getToastStyles();

  return (
    <div data-component-id="src/components/ui/Toast.tsx:75:4" data-component-path="src/components/ui/Toast.tsx" data-component-line="75" data-component-file="Toast.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" className={`fixed top-4 right-4 z-50 transition-all duration-300 ${
      isVisible ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'
    }`}>
      <div data-component-id="src/components/ui/Toast.tsx:78:6" data-component-path="src/components/ui/Toast.tsx" data-component-line="78" data-component-file="Toast.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" className={`${bgColor} text-white px-6 py-4 rounded-lg shadow-lg flex items-center gap-3 min-w-[300px]`}>
        {icon}
        <span data-component-id="src/components/ui/Toast.tsx:80:8" data-component-path="src/components/ui/Toast.tsx" data-component-line="80" data-component-file="Toast.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22font-tt-norms-medium%22%7D" className="font-tt-norms-medium">{message}</span>
        <button data-component-id="src/components/ui/Toast.tsx:81:8" data-component-path="src/components/ui/Toast.tsx" data-component-line="81" data-component-file="Toast.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%7D"
          onClick={() => {
            setIsAnimating(false);
            setTimeout(onClose, 300);
          }}
          className={`ml-auto flex-shrink-0 ${hoverColor} rounded-full p-1 transition-colors`}
        >
          <X data-component-id="src/components/ui/Toast.tsx:88:10" data-component-path="src/components/ui/Toast.tsx" data-component-line="88" data-component-file="Toast.tsx" data-component-name="X" data-component-content="%7B%22elementName%22%3A%22X%22%2C%22className%22%3A%22w-4%20h-4%22%7D" className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default Toast;
